<template>
  <div class="experience-feed">
    <h2 class="feed-header">{{ t('title') }}</h2>
    <div v-if="cardExperiences.length" class="feed-list">
      <ExpCardRedesign
        v-for="(exp, index) in cardExperiences"
        :key="exp.id"
        :exp="exp"
        size="s"
        sogu-page="main"
        sogu-block="popular"
        :is-in-favorites="isExperienceFavorite(exp.id)"
        is-show-favorite-btn
        data-test-prefix="feed-item"
        :exp-snippet-index="index"
        @toggle-favorite="handleToggleFavorite"
      />
    </div>
    <div v-else class="feed-list">
      <ExpCardRedesignSkeleton v-for="i in skeletonCount" :key="i" :is-animated="loading" />
    </div>
    <div v-if="hasMoreExperiences" class="feed-actions">
      <UiButton
        class="show-more-button"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :text="t('showMore')"
        :is-loading="loading"
        @clicked="fetchData"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { getDisplayCurrencyQuery } from '@experience/core-display-currency-lib';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { ExpCardRedesign, ExpCardRedesignSkeleton } from '@experience/exp-card';
import { useI18n } from '@experience/i18n';
import { sendFavoriteMindboxEvent } from '@experience/mindbox-events';
import { onMounted, computed, ref } from 'vue';

import ru from '@/features/experienceFeed/i18n/ru';
import type { Experience, ExperienceFavoriteItem } from '@/shared/api/generated/api';
import {
  addExperienceToFavorite,
  getExperienceList,
  getExperiencesFavoritesList,
  removeExperienceFromFavorite,
} from '@/shared/api/generated/api';
import { useOffsetLimitPagination } from '@/shared/helpers/pagination';
import { useAnalytics } from '@/shared/lib/analytics';
import { mapExperienceToExpCard } from '@/shared/lib/experience/mapExperienceToExpCard';
import { useMovementTypeLabels } from '@/shared/lib/experience/useMovementTypeLabels';
import { useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { trackEvent } = useAnalytics();

const favorites = ref<ExperienceFavoriteItem[]>([]);

const loadFavorites = async () => {
  const { data } = await getExperiencesFavoritesList();
  favorites.value = data ?? [];
};

const rawExperiences = ref<Experience[]>([]);
const hasMoreExperiences = ref(false);

// Восемь карточек заполняют две строки desktop-сетки и не меняют состав выдачи.
const { limit, page, offset } = useOffsetLimitPagination(8);
const loading = ref(true);

const { isMobile, isTabletOnly } = useScreenSize();
const { movementTypeLabels } = useMovementTypeLabels();
const skeletonCount = computed(() => {
  if (isMobile.value) {
    return 1;
  }

  if (isTabletOnly.value) {
    return 2;
  }

  return 8;
});

const cardExperiences = computed(() =>
  rawExperiences.value.map((experience) => mapExperienceToExpCard(experience, movementTypeLabels.value)),
);

const isExperienceFavorite = (experienceId: number) => {
  return favorites.value.some((item) => item.item_id === experienceId);
};

const fetchData = async () => {
  loading.value = true;

  let response;

  try {
    [response] = await Promise.all([
      getExperienceList({
        query: {
          limit: limit.value,
          offset: offset.value,
          ...getDisplayCurrencyQuery(),
        },
      }),
      loadFavorites(),
    ]);
  } catch (e) {
    loading.value = false;

    throw e;
  }

  const { data } = response;

  rawExperiences.value = rawExperiences.value.concat(data?.results ?? []);
  hasMoreExperiences.value = Boolean(data?.next);
  page.value += 1;
  loading.value = false;
};

onMounted(() => fetchData());

// Блокирует повторное переключение избранного до завершения текущего запроса.
let favoritesToggling = false;

// Отправляет аналитику намерения и добавляет существующую популярную экскурсию в избранное.
const handleSetFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    void trackEvent('main:popular:experience:add-to-favorite.click', { experience_id: experienceId, action: 'add' });

    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'add',
      bootstrapData: getBootstrapData(),
      experience,
    });

    const favoriteItem = await addExperienceToFavorite({ path: { experienceId } });

    if (favoriteItem.data) {
      favorites.value = [...favorites.value, favoriteItem.data];
    }
  } finally {
    favoritesToggling = false;
  }
};

// Отправляет аналитику намерения и удаляет существующую популярную экскурсию из избранного.
const handleSetNotFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    void trackEvent('main:popular:experience:add-to-favorite.click', { experience_id: experienceId, action: 'remove' });

    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'remove',
      bootstrapData: getBootstrapData(),
      experience,
    });

    await removeExperienceFromFavorite({ path: { experienceId } });
    favorites.value = favorites.value.filter((item) => item.item_id !== experienceId);
  } finally {
    favoritesToggling = false;
  }
};

const handleToggleFavorite = (experienceId: number) => {
  const experience = rawExperiences.value.find((item) => item.id === experienceId);

  if (isExperienceFavorite(experienceId)) {
    void handleSetNotFavorite(experience);
    return;
  }

  void handleSetFavorite(experience);
};
</script>

<style lang="scss" scoped>
.experience-feed {
  width: 100%;
}

.feed-header {
  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.feed-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--alias-space-40) var(--alias-space-24);
  margin-top: var(--alias-space-32);

  @include adaptivity.tablet {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include adaptivity.mobile {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--alias-space-24);
    margin-top: var(--alias-space-24);
  }
}

.feed-actions {
  display: flex;
  justify-content: center;
  padding-top: var(--alias-space-40);

  @include adaptivity.tablet {
    justify-content: stretch;
    padding-top: var(--alias-space-32);
  }

  @include adaptivity.mobile {
    padding-top: var(--alias-space-24);
  }
}

.show-more-button {
  @include adaptivity.tablet {
    width: 100%;
  }
}
</style>
